/**
 * Minimal llmProviderService shim for ported features (e.g. VoxPont2 translation verification).
 *
 * WHAT: Maps the experimental `callLLMWithQuery(queryId, userMessage, options)` contract onto this
 *       repo's single backend LLM path (`callLLMAPI` via invokeLLMPrompt).
 * WHY: The experimental app routed registered "Query Manager" queries through llmProviderService.
 *      This repo standardizes on callLLMAPI, so we adapt rather than port the whole Query Manager.
 * INVARIANT: All LLM calls go through callLLMAPI (project rule). The queryId is used as the promptId;
 *      if no matching prompt is registered the callable rejects and the caller degrades gracefully.
 */

import { invokeLLMPrompt } from "services/llmService";

/**
 * @param {string} queryId Registered prompt/query id (used as callLLMAPI promptId).
 * @param {string} userMessage The user input for the query.
 * @param {{ module?: string, featureName?: string, skipRateLimit?: boolean, config?: object, variantSlug?: string }} [options]
 * @returns {Promise<{ output: string, meta: object|null }>}
 */
async function callLLMWithQuery(queryId, userMessage, options = {}) {
  return invokeLLMPrompt({
    promptId: queryId,
    userMessage,
    config: options.config,
    variantSlug: options.variantSlug,
  });
}

/**
 * Fire-and-forget warm-up ping that keeps the `callLLMAPI` Cloud Function
 * container hot.
 *
 * WHY: In a non-English CareStart3 session the first translated question is the
 *      first `chartmind-translate` call; with a cold container it pays a
 *      multi-second cold-start penalty. Pinging that exact query path on page
 *      load / post-consent means the real first turn hits a warm container.
 * INVARIANT: best-effort only — never awaited by callers, never surfaces errors,
 *      and adds no user-perceived latency. A failed warm-up just means the first
 *      real call pays the cold start, exactly as the previous no-op did.
 *
 * Note: Despite the promptId, chartmind-translate is the platform shared
 * clinical-conversation translator (CareStart voice), not ChartMind-specific —
 * see docs/STRATEGIC_COHERENCE_LEDGER.md SCL-037.
 *
 * Note: `callLLMWithQuery` is a module-level function, not a `this` method, so we
 * invoke it via the `llmProviderService` object (initialized below) which keeps it
 * spyable in tests and avoids any `this`-binding ambiguity.
 *
 * @returns {void}
 */
function warmUp() {
  console.debug("[LLM] warmUp fired — chartmind-translate ping");
  try {
    Promise.resolve(
      llmProviderService.callLLMWithQuery("chartmind-translate", "__warmup__", {
        module: "WarmUp",
        featureName: "carestart3_warmup",
      })
    ).catch(() => {
      // intentionally silent — warm-up is best-effort only
    });
  } catch (e) {
    // intentionally silent — a synchronous throw must not reach the caller
    void e;
  }
}

const llmProviderService = { callLLMWithQuery, warmUp };
export default llmProviderService;
export { callLLMWithQuery, warmUp, llmProviderService };
